import { FC, ReactNode, useCallback } from 'react';
import { Form, useField, useForm } from 'react-final-form';

import {
  Button,
  ProgressButton,
} from '@beeline/design-system-react/extracted-styles';

import { BuildingSelectItem } from 'src/components/BuildingSelect/BuildingSelect';
import { Panel } from 'src/components/Panel/Panel';
import { TownSelectItem } from 'src/components/TownSelect/TownSelect';

import { FormBuildingSelect } from '../BuildingSelect/FormBuildingSelect';
import { FloorSelectItem } from '../FloorSelect/FloorSelect';
import { FormFloorSelect } from '../FloorSelect/FormFloorSelect';
import { FormTownSelect } from '../TownSelect/FormTownSelect';

export interface OfficeSidesheetValues {
  town: TownSelectItem | undefined;
  building: BuildingSelectItem | undefined;
  floor: FloorSelectItem | undefined;
}

interface InnerProps {
  clear: () => void;
  showFloor?: boolean;
  filterTownOptions?: (option: TownSelectItem) => boolean;
  filterBuildingOptions?: (option: BuildingSelectItem) => boolean;
  filterFloorOptions?: (option: FloorSelectItem) => boolean;
}

const Inner: FC<InnerProps> = ({
  clear,
  showFloor,
  filterTownOptions,
  filterBuildingOptions,
  filterFloorOptions,
}) => {
  const { submit, change } = useForm();

  const apply = useCallback(async () => {
    const result = await submit();
    if (!result) throw new Error('Form is invalid');
  }, [submit]);

  const townId = useField<Partial<TownSelectItem> | undefined>('town').input
    .value?.value?.townId;
  const buildingId = useField<Partial<BuildingSelectItem> | undefined>(
    'building',
  ).input.value?.value?.buildingId;

  const handleChangeTown = useCallback(() => {
    change('building', undefined);
    change('floor', undefined);
  }, [change]);

  const handleChangeBuilding = useCallback(() => {
    change('floor', undefined);
  }, [change]);

  return (
    <Panel
      actions={
        <>
          <Button variant='outlined' onClick={clear} data-testid='resetOffice'>
            Сбросить
          </Button>
          <ProgressButton
            variant='contained'
            onClick={apply}
            data-testid='applyOffice'
            statusDelayTime={0}
          >
            Применить
          </ProgressButton>
        </>
      }
      testId='officeSidesheetPanel'
    >
      <FormTownSelect
        name='town'
        onChange={handleChangeTown}
        dataTestId='officeSidesheetTown'
        filterOptions={filterTownOptions}
      />
      <FormBuildingSelect
        name='building'
        onChange={handleChangeBuilding}
        townId={townId}
        disabled={!townId}
        dataTestId='officeSidesheetBuilding'
        filterOptions={filterBuildingOptions}
      />
      {showFloor ? (
        <FormFloorSelect
          name='floor'
          buildingId={buildingId}
          disabled={!buildingId}
          dataTestId='officeSidesheetFloor'
          filterOptions={filterFloorOptions}
        />
      ) : null}
    </Panel>
  );
};

export interface OfficeSidesheetContentProps {
  apply: (values: OfficeSidesheetValues) => Promise<void>;
  initialValues: OfficeSidesheetValues;
  clear: () => void;
  showFloor?: boolean;
  filterTownOptions?: (option: TownSelectItem) => boolean;
  filterBuildingOptions?: (option: BuildingSelectItem) => boolean;
  filterFloorOptions?: (option: FloorSelectItem) => boolean;
}

export const OfficeSidesheetContent: FC<OfficeSidesheetContentProps> = ({
  apply,
  initialValues,
  ...rest
}) => {
  return (
    <Form onSubmit={apply} initialValues={initialValues}>
      {() => <Inner {...rest} />}
    </Form>
  );
};

export interface OfficeSidesheetProps {
  children?: ReactNode;
}
